import React, { useState, useEffect, useRef } from "react";
import Typed from "typed.js";
import Header from "../Components/Header";
import Sidebar from "../Components/Sidebar";
import Footer from "../Components/Footer";
import { isMobile } from "react-device-detect";
import Chart from "react-apexcharts";
import RouteApi from "../Backend/Backend";
import {
  VerticalTimeline,
  VerticalTimelineElement,
} from "react-vertical-timeline-component";
import "react-vertical-timeline-component/style.min.css";
import he from "he"; // Import he for decoding HTML entities
import { useSelector } from "react-redux";
import { toast } from "react-toastify"; // Import Toastify for notifications
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

const Home = () => {
  const { id } = useParams(); // Retrieve the dynamic id from the route params
  const {
    getStudents,
    students,
    tasks,
    generateAiTask,
    aiResponse,
    editTask,
    submitCreateTask,
    getTasks,
    pendingConfirmation,
    setPendingConfirmation,
    setAiResponse,
    handleEditStudent,
  } = RouteApi();

  const getTimeOfDay = () => {
    const hours = new Date().getHours();
    if (hours < 12) {
      return "AM";
    } else if (hours < 18) {
      return "PM";
    } else {
      return "Evening";
    }
  };

  const [token_id, setTokenId] = useState("");
  const [task_id, setTaskId] = useState("");
  const [task_description, setTaskDescription] = useState("");
  const [updatedTaskData, setupdatedTaskData] = useState({});

  const [isLoading, setIsLoading] = useState(true);
  const [isTypingComplete, setIsTypingComplete] = useState(false); // Track typing completion
  const typedRef = useRef(null);
  const [formattedResponse, setFormattedResponse] = useState("");

  useEffect(() => {
    if (aiResponse) {
      setIsLoading(false);
      setIsTypingComplete(false); // Reset state when a new response comes

      // Format response: Convert \n to <br />
      const formattedText = aiResponse.replace(/\n/g, "<br />");
      setFormattedResponse(formattedText);
    }
  }, [aiResponse]);

  useEffect(() => {
    if (formattedResponse && typedRef.current) {
      const options = {
        strings: [formattedResponse],
        typeSpeed: 20,
        showCursor: true,
        cursorChar: "|",
        loop: false,
        backSpeed: 0, // No erasing
        fadeOut: false,
        onComplete: () => setIsTypingComplete(true), // Set state when typing is done
      };

      const typed = new Typed(typedRef.current, options);

      return () => {
        typed.destroy();
      };
    }
  }, [formattedResponse]);

  const [search, setSearch] = useState(""); // 🔍 Search State

  // Keep this request scoped to the dashboard. RouteApi is also used by the
  // header and footer, so loading the full directory globally created several
  // concurrent database requests and could leave this table with an error
  // response instead of its records.
  useEffect(() => {
    getStudents();
  }, [id]);

  const populationByClassGroup = (students || []).reduce(
    (population, student) => {
      const classGroup = String(student?.stud_class || "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");

      if (["JSS1", "JSS2", "JSS3", "SSS1", "SSS2", "SSS3"].includes(classGroup)) {
        population.secondary += 1;
      } else if ([
        "NUR1", "NUR2", "KG1", "KG2", "PLAYGROUP",
        "GRADE1", "GRADE2", "GRADE3", "GRADE4", "GRADE5", "GRADE6",
        "BASIC1", "BASIC2", "BASIC3", "BASIC4", "BASIC5", "BASIC6"
      ].includes(classGroup)) {
        population.primary += 1;
      }
      return population;
    },
    { primary: 0, secondary: 0 }
  );
  const primaryPopulation = populationByClassGroup.primary;
  const secondaryPopulation = populationByClassGroup.secondary;
  const totalPopulation = primaryPopulation + secondaryPopulation;
  const hasPopulation = totalPopulation > 0;

  const config = {
    series: hasPopulation ? [primaryPopulation, secondaryPopulation] : [1],
    options: {
      labels: hasPopulation ? ["Primary", "Secondary"] : ["Empty"],
      chart: {
        type: "donut",
        toolbar: {
          show: false,
        },
      },
      legend: {
        show: false,
      },
      fill: {
        colors: hasPopulation ? ["#ff9ff3", "#c6c5ff"] : ["#57606f"],
      },
      dataLabels: {
        enabled: false,
      },
      cutout: 200,
      cutoutPercentage: "200%",
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        animateScale: true,
        animateRotate: true,
      },
      tooltips: {
        enabled: true,
        intersect: false,
        mode: "nearest",
        bodySpacing: 5,
        yPadding: 10,
        xPadding: 10,
        caretPadding: 0,
        displayColors: false,
        cornerRadius: 4,
        footerSpacing: 0,
        titleSpacing: 0,
      },
      plugins: {
        legend: {
          display: false,
          show: true,
        },
      },
      responsive: [
        {
          series:
            hasPopulation ? [primaryPopulation, secondaryPopulation] : [1],
          breakpoint: 400,
          options: {
            labels:
              hasPopulation ? ["Primary", "Secondary"] : ["Empty"],
            chart: {
              width: 130,
              height: 130,
            },
            legend: {
              show: false,
            },
          },
        },
      ],
    },
  };

  const filteredStudents = (
    students?.filter((student) => {
      const searchTerm = search.trim().toLowerCase();
      // Imported values are not guaranteed to be strings. Normalising them
      // makes the search reliable for every record and keeps an empty search
      // from accidentally filtering all rows.
      return [student.fstname, student.lstname, student.stud_id, student.stud_class,
        student.email_addd, student.phone_num]
        .some((value) => String(value ?? "").toLowerCase().includes(searchTerm));
    }) || []
  ).slice(0, 100);
  

  useEffect(() => {
    console.log(updatedTaskData);
  }, [updatedTaskData]);

  const user = useSelector((state) => state.auth?.user);

  // Get user details
  const fname = user?.ghostuser || ""; // Default to empty if not available
  const email = user?.email || "";
  const phone = user?.phone || "";

  const [taskData, setTaskData] = useState({
    task_type: "",
    task_name: "",
    reminder: false,
    task_description: "",
  });

  // Handle Input Changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTaskData({
      ...taskData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if fname, email, or phone is empty
    if (!fname || !email || !phone) {
      toast.error("Please provide your full details (Name, Email, and Phone).");
      return;
    }

    // Prepare data to be sent, including user info and task data
    const taskPayload = {
      ...taskData, // Spread the existing task data
      fname,
      email,
      phone,
    };

    try {
      const response = await submitCreateTask(taskPayload); // Pass taskPayload to backend
      //alert("Task created successfully!");
      toast.success("Task created successfully!");
      getTasks();
      console.log(response);
    } catch (error) {
      //alert("Failed to create task.");
      toast.error("Failed to create task.");
    }
  };

  const [selectedStudent, setSelectedStudent] = useState(null);

  const handleViewProfile = (student) => {
    setSelectedStudent(student);
  };

  const [formData, setFormData] = useState({
      stud_id: "",
      fstname: "",
      lstname: "",
      email_addd: "",
      email_add2: "",
      phone_num: "",
      phone_num2: "",
      stud_class: "",
      stud_class_depart: "",
      school: "",
      sex: "",
      locate_city: ""
    });
  
    useEffect(() => {
      if (selectedStudent) {
        setFormData({
          stud_id: selectedStudent.stud_id || "",
          fstname: selectedStudent.fstname || "",
          lstname: selectedStudent.lstname || "",
          email_addd: selectedStudent.email_addd || "",
          email_add2: selectedStudent.email_add2 || "",
          phone_num: selectedStudent.phone_num || "",
          phone_num2: selectedStudent.phone_num2 || "",
          stud_class: selectedStudent.stud_class || "",
          stud_class_depart: selectedStudent.stud_class_depart || "",
          school: selectedStudent.school || "",
          sex: selectedStudent.sex || "",
          locate_city: selectedStudent.locate_city || ""
        });
      }
    }, [selectedStudent]);
  
    const handleEditChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    };
  
    const handleEditSubmit = async (e) => {
          e.preventDefault();
          const result = await handleEditStudent(formData, selectedStudent.id);
          if (result?.success) {

            if (typeof getStudents === "function") {   // ✅ SAFE CHECK
              await getStudents();
            }

              Swal.fire({
                  text: "Student updated successfully!",
                  icon: "success",
                  confirmButtonText: "Ok, got it!",
                  customClass: { confirmButton: "btn fw-bold btn-success" },
              });
          } else {
              Swal.fire({
                  text: result.message || "Failed to update student.",
                  icon: "warning",
                  confirmButtonText: "Ok, got it!",
                  customClass: { confirmButton: "btn fw-bold btn-warning" },
              });
          }
      };
  
  
  return (
    <div className="bg-back">
      <Header />
      <Sidebar />

      <div className="docs-wrapper bg-back">
        <div className="container-fluid px-4 px-lg-6 pt-lg-10 pt-8 pb-10 pb-lg-10">
          <div className="row gx-6 gy-4 align-items-center">
            <div className="d-flex align-items-center">
              <div className="col">
                <div className="fs-10 text-q2 fw-medium">
                <i className="bi bi-stars text-warning"></i> Good {getTimeOfDay()}. Boss!
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div
                className="card bg-white shadow-none"
                style={{ borderRadius: "18px" }}
              >
                <div className="card-header shadow-none">
                  <span className="fs-n text-dark">
                    <i class="bi bi-pin-angle-fill text-danger"></i> Task
                    Workflow
                  </span>
                </div>
                <div className="card-body px-4">
                  {!tasks ? (
                    <div className="">
                      <div className="text-center mb-8">
                        <img
                          src="../assets/images/background/empty.png"
                          alt=""
                          width={isMobile ? "150px" : "60%"}
                          class="image"
                        />
                        <div className="fs-4 text-dark fw-medium">
                          No task workflow available
                        </div>
                      </div>
                    </div>
                  ) : (
                    <VerticalTimeline
                      lineColor="#000"
                      layout="1-column-left"
                      className="mt-n4"
                    >
                      {tasks.length === 0 ? (
                        <div className="">
                          <div className="text-center">
                            <img
                              src="../assets/images/background/empty.png"
                              alt=""
                              width={isMobile ? "150px" : "60%"}
                              class="image"
                            />
                            <div className="fs-4 text-dark fw-medium">
                              No task workflow available
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {tasks.map((task) => (
                            <VerticalTimelineElement
                              className="vertical-timeline-element--work shadow-none mb-lg-n6 cursor-pointer"
                              contentStyle={{
                                background: "#40407a",
                                color: "#fff",
                                boxShadow: "none",
                                paddingTop: "1px",
                                paddingBottom: "0px",
                              }}
                              iconStyle={{
                                background: "#40407a",
                                color: "#fff",
                                border: 0,
                                padding: 0,
                              }}
                              icon={
                                <div className="text-center">
                                  <i className="bi bi-bezier fs-t text-center"></i>
                                </div>
                              }
                            >
                              <div
                                className=""
                                data-bs-toggle="offcanvas"
                                data-bs-target="#run_task"
                                aria-controls="offcanvasBottom"
                                onClick={() => {
                                  generateAiTask(task.token_id, null);
                                  setTokenId(task.token_id);
                                  setTaskId(task.id);
                                  setTaskDescription(task.task_description);
                                  setupdatedTaskData({
                                    task_name: task.task_name,
                                    task_type: task.task_type,
                                    task_description: task_description,
                                    status: task.status,
                                    reminder: task.reminder,
                                    task_priority: task.task_priority,
                                  });
                                  setIsLoading(true);
                                  setFormattedResponse("");
                                }}
                              >
                                <div className="fs-4 fw-medium text-white mt-2">
                                  {task.task_name}
                                </div>
                                <p className="fw-light text-white fs-5 mt-1">
                                  {he.decode(task.task_description)}
                                </p>
                              </div>
                            </VerticalTimelineElement>
                          ))}
                        </>
                      )}
                    </VerticalTimeline>
                  )}

                  <div className="mt-2 text-center cursor-pointer">
                    <button className="btn btn-sm btn-purple4 text-white fw-medium rounded-3">
                      View More
                    </button>

                    <button
                      className="btn btn-dark shadow-none btn-sm rounded-3 ms-6"
                      data-bs-toggle="modal"
                      data-bs-target="#create_task"
                    >
                      Create Task
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card bg-white shadow-none">
                <div className="card-header shadow-none border-0">
                  <span className="fs-n text-dark">
                    <i className="bi bi-graph-up-arrow text-primary"></i>{" "}
                    Student Population
                  </span>
                </div>
                <div className="card-body mb-14">
                  <div className="col-12">
                    <Chart
                      options={config.options}
                      series={config.series}
                      type="donut"
                      height={220}
                    />

                    <div className="text-center mt-n19">
                      <span className="fs-2 text-dark fw-medium">
                        {totalPopulation}
                      </span>
                    </div>
                  </div>

                  <div className="col-12 text-center mb-n15 mt-9 fs-4 text-dark">
                    <button className="btn btn-sm primary"></button> Primary{" "}
                    <button className="btn btn-sm secondary"></button> Secondary
                  </div>
                </div>

                <div
                  className="card-footer border-0 text-center cursor-pointer"
                  style={{
                    borderBottomRightRadius: "18px",
                    borderBottomLeftRadius: "18px",
                  }}
                >
                  <button className="btn btn-sm btn-dark fw-medium rounded-3">
                    View Details
                  </button>
                </div>
              </div>
            </div>

            <div className="col-lg-12 pt-4">
              <div
                className="card bg-white shadow-none px-lg-3"
                style={{ borderRadius: "18px" }}
              >
                <div className="card-header shadow-none border-0 py-0 mt-4">
                  <span className="fs-n text-dark">
                    <i className="bi bi-menu-button-wide-fill text-success"></i>{" "}
                    Student Control Console
                  </span>
                </div>

                {/* 🔹 Search Input */}
                <div className="row gy-2 card-header shadow-none border-0 py-0 mt-4">
                  <div className="col-lg-6 position-relative">
                    <input
                      className="form-control form-control-md text-dark fs-4 rounded-pill ps-8"
                      placeholder="Search students by name, email, or phone"
                      value={search}
                      onChange={(e) => 
                        setSearch(e.target.value)}
                      style={{ paddingLeft: "2.5rem" }} // Ensure padding for icon space
                    />
                    <i
                      className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-5 text-muted"
                      style={{ fontSize: "1.5rem" }}
                    ></i>
                  </div>

                  <div className="col-lg-2"></div>

                  <div className="col-lg-4 text-end">
                    <button
                      type="button"
                      className="btn btn-purple4 btn-md text-white rounded-4"
                    >
                      <i className="bi bi-chat-dots fs-3"></i> Send Message
                    </button>
                  </div>
                </div>

                <div className="card-body py-0 mt-4 mb-6 px-2 py-lg-0 mt-lg-6 mb-lg-6 px-lg-6">
                  <div className="table-responsive table-card"
                  style={{
                    borderRadius: "10px",
                    scrollbarWidth: "thin",
                  }}>
                    <table
                      className="table table-hover table-border table-centered table-nowrap table-align-middle mb-0"
                      style={{ fontSize: "1.05rem" }}
                    >
                      <thead>
                        <tr>
                          <th className="fw-medium text-dark">#</th>
                          <th className="fw-medium text-dark">Name</th>
                          <th className="fw-medium text-dark">Student ID</th>
                          <th className="fw-medium text-dark">Class</th>
                          <th className="fw-medium text-dark">Email</th>
                          <th className="fw-medium text-dark">Phone</th>
                          <th className="fw-medium text-dark">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.length > 0 ? (
                          filteredStudents.map((student, index) => (
                            <tr key={student.id} className="gy-3 fs-5">
                              <th
                                scope="row"
                                className="fw-medium text-dark py-3"
                              >
                                {index + 1}
                              </th>
                              <td className="text-dark py-3">
                                {student.lstname + " " + student.fstname}
                              </td>
                              <td className="text-dark py-3">
                                {student.stud_id}
                              </td>
                              <td className="text-dark py-3">
                                {student.stud_class}
                              </td>
                              <td className="text-dark py-3">
                                {student.email_addd}
                              </td>
                              <td className="text-dark py-3">
                                {student.phone_num}
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-purple text-white rounded-3"
                                  data-bs-toggle="modal"
                                  data-bs-target="#students"
                                  onClick={() => handleViewProfile(student)}
                                >
                                  View
                                </button>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-purple3 text-dark rounded-3 ms-n5"
                                  data-bs-toggle="modal"
                                  data-bs-target="#open_editstud"
                                  onClick={() => handleViewProfile(student)}
                                >
                                  Edit
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="7"
                              className="text-center text-muted py-4"
                            >
                              No students found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Footer />

            <div
              className="modal fade"
              id="students"
              tabindex="-1"
              role="dialog"
              aria-labelledby="students"
              aria-hidden="true"
            >
              <div
                className={
                  isMobile
                    ? "modal-dialog modal-dialog-centered modal-fullscreen"
                    : "modal-dialog modal-md modal-dialog-centered"
                }
                role="document"
              >
                <div
                  className={isMobile ? "modal-content" : "modal-content"}
                  style={
                    isMobile
                      ? { border: "#fff" }
                      : { borderRadius: "30px", border: "#fff" }
                  }
                >
                  <div className="modal-header border-0 p-2">
                    <div className="col-lg-12 text-center">
                      <img
                        src="../assets/images/background/back.jpg"
                        alt="background"
                        style={{
                          width: "100%",
                          height: "10% !important",
                          borderRadius: "30px",
                        }}
                        class="student-image"
                      />
                    </div>
                  </div>
                  <div className="modal-body text-start text-dark fs-5">
                    <div className="position-relative mt-n10">
                      <span className="avatar avatar-xl avatar-dark rounded-circle">
                        <span className="avatar-initials rounded-circle fs-t fw-medium">
                          {selectedStudent?.lstname?.charAt(0).toUpperCase()}
                          {selectedStudent?.fstname?.charAt(0).toUpperCase()}
                        </span>
                      </span>
                    </div>
                    <div className="row align-items-center py-0">
                      <div className="col-10 text-start mt-2">
                        <div className="fs-t col-12 fw-medium">
                          {selectedStudent?.lstname}{" "}
                          {selectedStudent?.fstname}
                        </div>
                        <div className="text-start fs-4 text-dark">
                          {selectedStudent?.stud_id}
                        </div>
                      </div>
                      <div className="col-2">
                        <div className="fs-3 bg-light-dark text-dark rounded-pill py-1 text-center">
                          {selectedStudent?.stud_class}
                        </div>
                      </div>
                    </div>

                    <div className="hstack text-center justify-content-center mt-4 gap-2">
                      <div className="fs-4 py-1 text-center">
                        {selectedStudent?.email_addd}
                      </div>
                      <div className="vr"></div>
                      <div className="fs-4 py-1 text-center">
                        {selectedStudent?.phone_num}
                      </div>
                    </div>
                  </div>
  
                  <div className="modal-footer border-0 mt-n4">
                    <div className="col-12 d-grid px-8">
                    <button className="btn btn-lg btn-dark rounded-pill">
                     Get In Touch
                    </button>
                  </div>
                  </div>
                </div>
              </div>
            </div>
        
        </div>
      </div>

      <div
        class="offcanvas offcanvas-bottom offcanvas-static bg-white"
        tabindex="-1"
        id="run_task"
        aria-labelledby="offcanvasBottomLabel"
        style={
          isMobile
            ? {
                width: "500px",
                height: "1000px",
              }
            : {
                borderTopRightRadius: "30px",
                borderTopLeftRadius: "30px",
                width: "500px",
                height: "1000px",
                bottom: "0",
                left: "auto",
                right: "30px",
              }
        }
      >
        <div class="offcanvas-header">
          <h4 class="offcanvas-title" id="offcanvasBottomLabel">
            <i class="bi bi-stars text-warning fs-4"></i> Smart Ai Console
          </h4>
          <button
            type="button"
            class="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          ></button>
        </div>
        <div class="offcanvas-body small">
          <div className="fs-4 text-dark">
            {isLoading ? (
              <div className="loading-text text-dark fs-4 fw-medium">
                Analyzing...
              </div>
            ) : (
              <>
                <span
                  ref={typedRef}
                  className="typed-response"
                  dangerouslySetInnerHTML={{ __html: formattedResponse }}
                ></span>

                {/* */}
                {pendingConfirmation && (
                  <div className="mt-3">
                    <button
                      className="btn btn-sm shadow-none btn-success rounded-3 me-2"
                      onClick={() => {
                        generateAiTask(pendingConfirmation.token_id, "yes");
                        setIsLoading(true);
                        setFormattedResponse("");
                        setPendingConfirmation(null);
                      }}
                    >
                      Yes
                    </button>
                    <button
                      className="btn btn-sm shadow-none btn-danger rounded-3"
                      onClick={() => {
                        setAiResponse("❌ Smart AI task execution canceled.");
                        setPendingConfirmation(null);
                      }}
                    >
                      No
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="offcanvas-footer ">
          <div className="px-lg-4 px-4 mb-6 mt-3">
            <label
              htmlFor="taskDescription"
              className="form-label fs-4 text-dark fw-medium"
            >
              Change Task Description
            </label>
            <div class="d-flex align-items-center">
              <textarea
                className="pe-9 form-control form-control-md bg-back fs-4 text-dark rounded-3"
                id="taskDescription"
                name="task_description"
                rows="2"
                value={task_description}
                placeholder="Task description"
                onChange={(e) => {
                  setTaskDescription(e.target.value);
                  setupdatedTaskData((prevData) => ({
                    ...prevData,
                    task_description: e.target.value,
                  }));
                }}
              ></textarea>

              <button
                type="button"
                onClick={() => {
                  editTask(task_id, updatedTaskData);
                  generateAiTask(token_id, null);
                  getTasks();
                }}
                className="btn btn-sm btn-dark rounded-pill pe-3 ms-n9"
              >
                <i className="bi bi-send fs-4"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        class="offcanvas offcanvas-bottom offcanvas-static bg-white"
        data-bs-scroll="true"
        data-bs-backdrop="false"
        tabindex="-1"
        id="run_task"
        aria-labelledby="offcanvasBottomLabel"
        style={
          isMobile
            ? {}
            : {
                borderTopRightRadius: "30px",
                borderTopLeftRadius: "30px",
                width: "500px",
                height: "1000px",
                bottom: "0",
                left: "auto",
                right: "30px",
              }
        }
      >
        <div class="offcanvas-header">
          <h4 class="offcanvas-title" id="offcanvasBottomLabel">
            <i class="bi bi-stars text-warning fs-4"></i> Smart Ai Console
          </h4>
          <button
            className="btn btn-dark btn-icon rounded-br-lg2 rounded-bl-sm2 rounded-t-lg2"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i className="bi bi-x-lg fw-bold fs-3 text-white"></i>
          </button>
        </div>
        <div class="offcanvas-body small">
          <div className="fs-4 text-dark">
            {isLoading ? (
              <div className="loading-text text-dark fs-4 fw-medium">
                Analyzing...
              </div>
            ) : (
              <>
                <span
                  ref={typedRef}
                  className="typed-response"
                  dangerouslySetInnerHTML={{ __html: formattedResponse }}
                ></span>

                {/* */}
                {pendingConfirmation && (
                  <div className="mt-3">
                    <button
                      className="btn btn-sm shadow-none btn-success rounded-3 me-2"
                      onClick={() => {
                        generateAiTask(pendingConfirmation.token_id, "yes");
                        setIsLoading(true);
                        setFormattedResponse("");
                        setPendingConfirmation(null);
                      }}
                    >
                      Yes
                    </button>
                    <button
                      className="btn btn-sm shadow-none btn-danger rounded-3"
                      onClick={() => {
                        setAiResponse("❌ Smart AI task execution canceled.");
                        setPendingConfirmation(null);
                      }}
                    >
                      No
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="offcanvas-footer ">
          <div className="px-lg-4 mb-6 mt-3">
            <label
              htmlFor="taskDescription"
              className="form-label fs-4 text-dark fw-medium"
            >
              Change Task Description
            </label>
            <div class="d-flex align-items-center">
              <textarea
                className="pe-7 form-control form-control-md bg-back fs-4 text-dark rounded-3"
                id="taskDescription"
                name="task_description"
                rows="2"
                value={task_description}
                placeholder="Task description"
                onChange={(e) => {
                  setTaskDescription(e.target.value);
                  setupdatedTaskData((prevData) => ({
                    ...prevData,
                    task_description: e.target.value,
                  }));
                }}
              ></textarea>

              <button
                type="button"
                onClick={() => {
                  editTask(task_id, updatedTaskData);
                  generateAiTask(token_id, null);
                  getTasks();
                }}
                className="btn btn-sm btn-dark rounded-pill pe-3 ms-n9"
              >
                <i className="bi bi-send fs-4"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="create_task"
        tabindex="-1"
        role="dialog"
        data-bs-backdrop="static"
        aria-labelledby="exampleModalCenterTitle"
        aria-hidden="true"
      >
        <div
          className={
            isMobile
              ? "modal-dialog modal-dialog-centered modal-fullscreen"
              : "modal-dialog modal-lg modal-dialog-centered"
          }
          role="document"
        >
          <div
            className={isMobile ? "modal-content" : "modal-content"}
            style={
              isMobile
                ? { border: "#fff" }
                : { borderRadius: "30px", border: "#fff" }
            }
          >
            <div className="modal-header border-0 mt-4">
              <div className="fs-t1 text-purple2">
                <i className="bi bi-plus"></i> Create New Task
              </div>

              <button
                className="btn btn-dark btn-icon rounded-br-lg2 rounded-bl-sm2 rounded-t-lg2"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="bi bi-x-lg fw-bold fs-3 text-white"></i>
              </button>
            </div>
            <div className="modal-body py-0 mt-2 mb-4">
              <div className="px-lg-10 px-2">
                <form className="row" onSubmit={handleSubmit}>
                  <div className="col-lg-6 mb-3">
                    <label
                      htmlFor="taskType"
                      className="form-label fs-3 text-dark fw-light"
                    >
                      Type of Task
                    </label>
                    <select
                      className="form-select form-select-md text-dark bg-back fs-3 rounded-pill"
                      id="taskType"
                      name="task_type"
                      value={taskData.task_type}
                      required
                      onChange={handleChange}
                      aria-label="Select task type"
                    >
                      <option>Select Task Type</option>
                      <option value="message">Send Message</option>
                      <option value="exercise">Create Exercise</option>
                      <option value="note">Create Note</option>
                      <option value="event">Upload Event</option>
                      <option value="dispatch">Dispatch Result</option>
                    </select>
                  </div>

                  <div className="col-lg-6 mb-3">
                    <label
                      htmlFor="taskPriority"
                      className="form-label fs-3 text-dark fw-light"
                    >
                      Priority
                    </label>
                    <select
                      className="form-select form-select-md text-dark bg-back fs-3 rounded-pill"
                      id="taskPriority"
                      name="task_priority"
                      onChange={handleChange}
                      value={taskData.task_priority}
                      aria-label="Select priority"
                    >
                      <option value="urgent">Urgent</option>
                      <option value="normal">Normal</option>
                      <option value="low">Low Priority</option>
                    </select>
                  </div>

                  <div className="col-lg-12 mb-3">
                    <label
                      htmlFor="taskName"
                      className="form-label fs-3 text-dark fw-light"
                    >
                      Task Name
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-md text-dark bg-back fs-3 rounded-pill"
                      id="taskName"
                      name="task_name"
                      value={taskData.task_name}
                      onChange={handleChange}
                      placeholder="Enter task name"
                    />
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor="taskReminder"
                      className="form-label fs-3 text-dark fw-light"
                    >
                      Need a reminder?{" "}
                    </label>
                    <input
                      type="checkbox"
                      className="form-check-input rounded-3 ms-1 mt-2"
                      id="taskReminder"
                      name="reminder"
                      checked={taskData.reminder}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-lg-12 mb-3">
                    <label
                      htmlFor="taskDescription"
                      className="form-label fs-3 text-dark fw-light"
                    >
                      Task Description
                    </label>
                    <textarea
                      className="form-control form-control-md bg-back text-dark fs-3 rounded-3"
                      id="taskDescription"
                      name="task_description"
                      value={taskData.task_description}
                      onChange={handleChange}
                      rows="2"
                      placeholder="Enter task description"
                    ></textarea>
                  </div>

                  <div className="d-flex justify-content-center mt-3">
                    <button
                      type="submit"
                      className="btn btn-purple btn-lg rounded-pill"
                    >
                      Create Task
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
              className="modal fade"
              id="open_editstud"
              tabindex="-1"
              role="dialog"
              aria-labelledby="open_editstud"
              aria-hidden="true"
          >
            <div
              className={
              isMobile
                  ? "modal-dialog modal-dialog-centered modal-fullscreen"
                  : "modal-dialog modal-lg modal-dialog-centered"
              }
              role="document"
          >
              <div
              className={isMobile ? "modal-content" : "modal-content"}
              style={
                  isMobile
                  ? { border: "#fff" }
                  : { borderRadius: "30px", border: "#fff" }
              }
              >
                <div className="modal-header border-0 mt-2 px-6">
                    <div className="fs-t text-purple2">
                      <i className="bi bi-pencil fs-t"></i> Edit {formData.fstname}'s Details
                    </div>
      
                    <button
                      className="btn btn-dark btn-icon rounded-br-lg2 rounded-bl-sm2 rounded-t-lg2"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    >
                      <i className="bi bi-x-lg fw-bold fs-3 text-white"></i>
                    </button>
                  </div>
                <div className="modal-body py-2 px-lg-8 mt-lg-2 mt-2">

                <form onSubmit={handleEditSubmit} className="row gy-3 py-3">

                  <div className="col-lg-6 mb-4">
                  <label className="fs-4 text-dark mb-2">First Name</label>
                  <input
                      type="text"
                      name="fstname"
                      value={formData.fstname}
                      onChange={handleEditChange}
                      placeholder="Enter first name"
                      className="form-control form-control-md text-dark fs-4 rounded-pill"
                      required
                  />
                  </div>
      
                  <div className="col-lg-6 mb-4">
                  <label className="fs-4 text-dark mb-2">Last Name</label>
                  <input
                      type="text"
                      name="lstname"
                      value={formData.lstname}
                      onChange={handleEditChange}
                      placeholder="Enter last name"
                      className="form-control form-control-md text-dark fs-4 rounded-pill"
                      required
                  />
                  </div>
      
                  <div className="col-lg-6 mb-4">
                  <label className="fs-4 text-dark mb-2">Primary Email</label>
                  <input
                      type="email"
                      name="email_addd"
                      value={formData.email_addd}
                      onChange={handleEditChange}
                      placeholder="Enter primary email"
                      className="form-control form-control-md text-dark fs-4 rounded-pill"
                  />
                  </div>
      
                  <div className="col-lg-6 mb-4">
                  <label className="fs-4 text-dark mb-2">Secondary Email</label>
                  <input
                      type="email"
                      name="email_add2"
                      value={formData.email_add2}
                      onChange={handleEditChange}
                      placeholder="Enter secondary email"
                      className="form-control form-control-md text-dark fs-4 rounded-pill"
                  />
                  </div>
      
                  <div className="col-lg-6 mb-4">
                  <label className="fs-4 text-dark mb-2">Phone 1</label>
                  <input
                      type="text"
                      name="phone_num"
                      value={formData.phone_num}
                      onChange={handleEditChange}
                      placeholder="Enter primary phone number"
                      className="form-control form-control-md text-dark fs-4 rounded-pill"
                  />
                  </div>
      
                  <div className="col-lg-6 mb-4">
                  <label className="fs-4 text-dark mb-2">Phone 2</label>
                  <input
                      type="text"
                      name="phone_num2"
                      value={formData.phone_num2}
                      onChange={handleEditChange}
                      placeholder="Enter secondary phone number"
                      className="form-control form-control-md text-dark fs-4 rounded-pill"
                  />
                  </div>
      
                  {/* Class as Select */}
                  <div className="col-lg-6 mb-4">
                  <label className="fs-4 text-dark mb-2">Class</label>
                  <select
                      name="stud_class"
                      value={formData.stud_class}
                      onChange={handleEditChange}
                      className="form-control form-control-md text-dark fs-4 rounded-pill"
                      required
                  >
                      <option value="">Select Class</option>
                      <option value="Preparatory 1">Preparatory 1</option>
                      <option value="Preparatory 2">Preparatory 2</option>
                      <option value="Reception 1">Reception 1</option>
                      <option value="Reception 2">Reception 2</option>
                      <option value="PlayGroup">PlayGroup</option>
                      <option value="NUR 1">NUR 1</option>
                      <option value="NUR 2">NUR 2</option>
                      <option value="KG 1">KG 1</option>
                      <option value="KG 2">KG 2</option>
                      <option value="Grade 1">Grade 1</option>
                      <option value="Grade 2">Grade 2</option>
                      <option value="Grade 3">Grade 3</option>
                      <option value="Grade 4">Grade 4</option>
                      <option value="Grade 5">Grade 5</option>
                      <option value="Grade 6">Grade 6</option>
                      <option value="Jss1">JSS 1</option>
                      <option value="Jss2">JSS 2</option>
                      <option value="Jss3">JSS 3</option>
                      <option value="sss1">SSS 1</option>
                      <option value="sss2">SSS 2</option>
                      <option value="sss3">SSS 3</option>
                  </select>
                  </div>
      
                  {/* Department as Select */}
                  <div className="col-lg-6 mb-4">
                  <label className="fs-4 text-dark mb-2">Department</label>
                  <select
                      name="stud_class_depart"
                      value={formData.stud_class_depart}
                      onChange={handleEditChange}
                      className="form-control form-control-md text-dark fs-4 rounded-pill"
                      required
                  >
                      <option value="">Select Department</option>
                      <option value="NONE">NONE</option>
                      <option value="SCIENCE">SCIENCE</option>
                      <option value="ART">ART</option>
                      <option value="HUMANITIES">HUMANITIES</option>
                      <option value="BUSINESS">BUSINESS</option>
                      <option value="COMMERCIAL">COMMERCIAL</option>
                  </select>
                  </div>
      
                  {/* School Level as Select */}
                  <div className="col-lg-6 mb-4">
                  <label className="fs-4 text-dark mb-2">Student ID</label>
                  <input
                      type="text"
                      name="stud_id"
                      value={formData.stud_id}
                      onChange={handleEditChange}
                      placeholder="Enter Student ID"
                      className="form-control form-control-md text-dark fs-4 rounded-pill"
                  />
                  </div>
      
                  {/* Gender */}
                  <div className="col-lg-6 mb-4">
                  <label className="fs-4 text-dark mb-2">Gender</label>
                  <select
                      name="sex"
                      value={formData.sex}
                      onChange={handleEditChange}
                      className="form-control form-control-md text-dark fs-4 rounded-pill"
                      required
                  >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                  </select>
                  </div>
      
                  <div className="col-lg-12 mb-4">
                  <label className="fs-4 text-dark mb-2">State/City</label>
                  <input
                      type="text"
                      name="locate_city"
                      value={formData.locate_city}
                      onChange={handleEditChange}
                      placeholder="Enter state or city"
                      className="form-control form-control-md text-dark fs-4 rounded-pill"
                  />
                  </div>
      
                  <div className="col-12 text-center mt-3">
                  <button type="submit" className="btn btn-purple btn-lg rounded-pill">
                      Save Changes
                  </button>
                  </div>
      
                </form>
      
                </div>
              </div>
            </div>
          </div>
      
    </div>
  );
};

export default Home;
